import { PhoneNumberUtil } from '@ecm/material';
import { Contract, KoType, SuspendedReason } from '@ecm/portfolio';

import { ConvertUtils } from '../../../utils/convert.utils';
import { ImageUtils } from '../../../utils/image.utils';
import { ContractUtils } from '../../api/contract/contractUtils';
import { StatusEnum } from '../../api/contract/status.enum';
import { TelcoSubTypeEnum } from '../../api/contract/telcoSubType.enum';
import { TelcoTypeEnum } from '../../api/contract/telcoType.enum';
import { BillSummaries } from '../../api/dashboard/billSummaries/billSummaries';
import { Member } from '../../api/family/member';
import { DashboardReco } from '../../api/reco/dashboardReco';
import { SuspendedViewModel } from '../../model/common/suspended.view-model';
import { BillItemViewModel } from '../../model/crossroads/bills/bill-item.view-model';
import { KoContractViewModel } from '../../model/crossroads/contracts-options/ko-contract.view-model';
import { MemberContractViewModel } from '../../model/crossroads/contracts-options/memberContract.view-model';
import { PushSubItemViewModel } from '../../model/home/pushSubItem.view-model';
import { SubItemViewModel } from '../../model/home/subItem.view-model';
import { KenobiCodes } from '../../model/tracking/kenobiCodes';
import { TrackedPage } from '../../model/tracking/trackedPage.enum';

export class CrossroadsMapperUtils {
    /**
     * Checks for unpaid contracts
     * @param {string} cid
     * @param {BillSummaries[]} billSummaries
     * @return boolean
     */
    public static isUnpaidBill(cid: string, billSummaries: BillSummaries[]): boolean {
        if (billSummaries) {
            for (const summary of billSummaries) {
                if (cid === summary.contractId && summary.billSummary && summary.billSummary.unpaidBill) {
                    return true;
                }
            }
        }
        return false;
    }

    /**
     * get SuspendedViewModel
     * @param {Contract} contract
     * @param {any} translation
     * @param {BillSummaries[]} billSummaries
     * @return {SuspendedViewModel}
     */
    public static getSuspendedViewModel(
        contract: Contract,
        translation: any,
        billSummaries?: BillSummaries[],
    ): SuspendedViewModel {
        let suspendedViewModel = new SuspendedViewModel();
        if (billSummaries) {
            for (const summary of billSummaries) {
                if (contract.cid === summary.contractId && summary.billSummary.unpaidBill) {
                    if (contract.telco.subType === TelcoSubTypeEnum.internet) {
                        suspendedViewModel = this.getSuspendedInfo(
                            translation['nonPaymentInternet'],
                            SuspendedReason.PRESAR,
                        );
                    } else if (
                        contract.telco.subType === TelcoSubTypeEnum.mobile ||
                        contract.telco.subType === TelcoSubTypeEnum.open
                    ) {
                        suspendedViewModel = this.getSuspendedInfo(translation['nonPaymentMobile']);
                    }
                }
            }
        }
        if (
            contract.telco.suspendedReason &&
            (contract.telco.suspendedReason === SuspendedReason.LOSS ||
                contract.telco.suspendedReason === SuspendedReason.THEFT)
        ) {
            suspendedViewModel = this.getSuspendedInfo(
                translation['lostAndStolen'],
                contract.telco.suspendedReason,
                contract.telco.publicNumber,
            );
        }
        return suspendedViewModel;
    }

    public static getSuspendedInfo(
        translation: any,
        telcoSuspendedReason?: SuspendedReason,
        mobileNumber?: string,
    ): SuspendedViewModel {
        let suspendedTitle: string = translation['title'];
        if (mobileNumber) {
            suspendedTitle = suspendedTitle.replace('{{mobileNumber}}', mobileNumber);
        }
        return Object.assign(new SuspendedViewModel(), {
            suspendedReason: telcoSuspendedReason ? telcoSuspendedReason : SuspendedReason.NONPAYMENT,
            unpaid: !(telcoSuspendedReason === SuspendedReason.THEFT || telcoSuspendedReason === SuspendedReason.LOSS),
            title: suspendedTitle,
            text: translation['text'],
            button: translation['button'],
        });
    }

    /**
     * get member family name
     * @param {Map<string, Member>} mapMembers
     * @param {string} cid
     * @return {MemberContractViewModel}
     */
    public static getMemberDisplayName(mapMembers: Map<string, Member>, cid: string): MemberContractViewModel {
        let displayName: MemberContractViewModel;
        const member: Member = mapMembers.get(cid);
        if (member) {
            for (const associatedContract of member.associatedContracts) {
                if (member.firstName && cid === associatedContract.installedContractId) {
                    displayName = new MemberContractViewModel(member.firstName);
                }
            }
        }
        return displayName;
    }

    /**
     * translate code type on telco.type and concatenate the phone number of publicNumber
     * @param {string} telcoType
     * @param {string} telcoSubType
     * @param {string} phoneNumber
     * @return {string} detail
     */
    public static getContractDetail(telcoType: string, telcoSubType: string, phoneNumber: string): string {
        let contractLabel: string;
        if (
            telcoSubType &&
            (TelcoTypeEnum[telcoType] !== TelcoTypeEnum.pro || TelcoTypeEnum[telcoType] !== TelcoTypeEnum.obs)
        ) {
            contractLabel = ContractUtils.subTypes.get(telcoSubType);
        } else {
            contractLabel = TelcoTypeEnum[telcoType];
        }
        return (contractLabel ? contractLabel : 'Ligne').concat(' ', PhoneNumberUtil.getFormattedPhone(phoneNumber));
    }

    /**
     * get reco of dashboardsReco
     * @param {DashboardReco[]} dashboardsReco
     * @param {string} cid
     * @return {PushSubItemViewModel}
     */
    public static getContractReco(dashboardsReco: DashboardReco[], cid: string): PushSubItemViewModel {
        const recoItems = dashboardsReco.filter((reco: DashboardReco) => {
            return reco.cid === cid;
        });
        let contractReco: PushSubItemViewModel = null;
        if (recoItems.length === 1) {
            contractReco = Object.assign(new PushSubItemViewModel(), {
                textPrimary: true,
                type: 'commercial',
                label: recoItems[0].content,
                cid: recoItems[0].cid,
                advTracker: recoItems[0].advTracker,
                code: recoItems[0].code,
                link: recoItems[0].link,
                zid: 'care-digital_contrats_marchand_contrats',
                zidMob: 'care-digital_contrats-mob_marchand_contrats',
                functionality: KenobiCodes.CTROPT.functionality,
            });
            if (Object.prototype.hasOwnProperty.call(recoItems[0], 'imgUrl')) {
                contractReco.pictoImage = ImageUtils.transformUrl(recoItems[0].imgUrl, '32');
            }
        }
        return contractReco;
    }

    /**
     * get last bill
     * @param {BillItemViewModel} billSummaries
     * @param {string} cid
     * @return {BillItemViewModel}
     */
    public static getBillItem(billSummaries: BillSummaries[], cid: string): BillItemViewModel {
        if (billSummaries) {
            for (const summary of billSummaries) {
                if (cid === summary.contractId && summary.billSummary) {
                    return summary.billSummary.lastBillStatus !== 'AUCUNE_FACTURE'
                        ? Object.assign(new BillItemViewModel(), {
                              lastBillStatus: true,
                              lastBillAmount: ConvertUtils.convertCentsToEuros(
                                  Number(summary.billSummary.lastBillAmount),
                              ),
                              lastBillDate: summary.billSummary.lastBillDate,
                          })
                        : new BillItemViewModel();
                }
            }
        }

        return undefined;
    }

    /**
     * Extract map of member from family.members with cid key
     * @param {Member[]} members
     * @return {Map<string, Member>} members map
     */
    public static extractMapMembersFromFamily(members: Member[]): Map<string, Member> {
        const membersMap: Map<string, Member> = new Map<string, Member>();
        if (members) {
            for (const member of members) {
                if (member.associatedContracts) {
                    for (const associatedContract of member.associatedContracts) {
                        if (associatedContract) {
                            membersMap.set(associatedContract.installedContractId, member);
                        }
                    }
                }
            }
        }
        return membersMap;
    }

    public static getFamilyAdvantages(contract: Contract, familyAdvantages: any): SubItemViewModel {
        return Object.assign(new SubItemViewModel(), {
            cid: contract.cid,
            label: familyAdvantages['subLabel'],
            picto: 'icon-Family-space',
            link: familyAdvantages['link'],
            trackingName: familyAdvantages['label'],
            trackingArea: 'care_selecteurcontrat_offre',
            trackedPage: TrackedPage.contractsAndOptions,
        });
    }

    public static getKoContract(koType: KoType, translation: any): KoContractViewModel {
        let koContract: KoContractViewModel = null;
        if (koType === KoType.TOTAL) {
            koContract = Object.assign(new KoContractViewModel(), {
                typeKoContract: KoType.TOTAL,
                textKoContract: translation['total'],
                hideButtonKoContract: true,
            });
        } else if (koType === KoType.PARTIAL) {
            koContract = Object.assign(new KoContractViewModel(), {
                typeKoContract: KoType.PARTIAL,
                textKoContract: translation['partial'],
                hideButtonKoContract: false,
            });
        }
        return koContract;
    }

    /**
     * return ko contract model for inprogress contract
     * @param {string} brand
     * @param {any} translation
     * @return KoContractViewModel
     */
    public static getInprogressContract(brand: string, translation: any): KoContractViewModel {
        return Object.assign(new KoContractViewModel(), {
            typeKoContract: StatusEnum.INPROGRESS,
            textKoContract: translation['text'],
            hideButtonKoContract: true,
            isWarningAlert: false,
            linkLabel: translation['labelLink'],
            orangeLinkLabel: translation['labelOrangeLink'],
            soshLinkLabel: translation['labelSoshLink'],
            link: brand === 'Orange' ? translation['orangeLink'] : translation['soshLink'],
        });
    }
}
